import React, { useRef, useEffect, useCallback } from 'react';
import '../styles/HighlightedTextarea.css';
import { setEditorCaret } from '../utils/editorCaret';
import { normalizeNamePlaceholders } from '../utils/emailPreview';

/**
 * HighlightedTextarea - Contenteditable with draggable [NAME] pills
 * Features live drop preview - text shifts to show where pill will land
 */
export default function HighlightedTextarea({
  value = '',
  onChange,
  onInput,
  onKeyDown,
  className = '',
  placeholder = '',
  ariaLabel,
  disabled = false,
  textareaRef,
}) {
  const editorRef = useRef(null);
  const dropIndicatorRef = useRef(null);
  const isDraggingRef = useRef(false);

  // Convert plain text to HTML with pills
  const textToHtml = useCallback((text) => {
    if (!text) return '';

    let html = normalizeNamePlaceholders(text)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');

    html = html.replace(
      /\[NAME\]/g,
      '<span class="name-pill" contenteditable="false" draggable="true" data-placeholder="NAME">[NAME]</span>'
    );

    html = html.replace(/\n/g, '<br>');

    return html;
  }, []);

  // Convert HTML back to plain text
  const htmlToText = useCallback((element) => {
    if (!element) return '';

    let text = '';
    const walk = (node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        text += node.textContent;
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        // Skip drop indicator
        if (node.classList?.contains('drop-indicator')) return;

        if (node.classList?.contains('name-pill')) {
          text += '[NAME]';
        } else if (node.tagName === 'BR') {
          text += '\n';
        } else if (node.tagName === 'DIV' && text.length > 0 && !text.endsWith('\n')) {
          text += '\n';
          node.childNodes.forEach(walk);
        } else {
          node.childNodes.forEach(walk);
        }
      }
    };

    element.childNodes.forEach(walk);
    return text;
  }, []);

  const hasLiteralNameToken = useCallback((element) => {
    if (!element) return false;

    const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);
    let node = walker.nextNode();
    while (node) {
      const pill = node.parentElement?.closest('.name-pill');
      if (
        (normalizeNamePlaceholders(node.textContent) !== node.textContent || /\[NAME\]/i.test(node.textContent))
        && !pill
      ) return true;
      node = walker.nextNode();
    }
    return false;
  }, []);

  const getCaretPrefix = useCallback((element) => {
    const selection = window.getSelection?.();
    if (!element || !selection?.rangeCount) return null;

    const range = selection.getRangeAt(0);
    if (range.startContainer !== element && !element.contains(range.startContainer)) {
      return null;
    }

    const beforeCaret = document.createRange();
    beforeCaret.selectNodeContents(element);
    try {
      beforeCaret.setEnd(range.startContainer, range.startOffset);
    } catch {
      return null;
    }
    // setEditorCaret walks text nodes (including pill text) and does not count
    // <br> elements, so measure in the same coordinate system.
    return beforeCaret.cloneContents().textContent;
  }, []);

  const selectionIntersectsNamePill = useCallback((range) => Array.from(
    editorRef.current?.querySelectorAll('.name-pill') || []
  ).some(pill => range.intersectsNode(pill)), []);

  // Synchronize external values and structurally hydrate literal tokens.
  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;

    const currentText = htmlToText(editor);
    if (currentText === value && !hasLiteralNameToken(editor)) return;

    const caretPrefix = getCaretPrefix(editor);
    const caretOffset = caretPrefix === null
      ? null
      : normalizeNamePlaceholders(caretPrefix).length;
    editor.innerHTML = textToHtml(value);
    if (caretOffset !== null) {
      setEditorCaret(editor, Math.min(caretOffset, value.length));
    }
  }, [value, textToHtml, htmlToText, hasLiteralNameToken, getCaretPrefix]);

  // Handle input changes
  const handleInput = useCallback((e) => {
    const newText = normalizeNamePlaceholders(htmlToText(editorRef.current));

    const syntheticEvent = {
      target: { value: newText },
      currentTarget: { value: newText },
    };

    onChange?.(syntheticEvent);
    onInput?.(syntheticEvent);
  }, [onChange, onInput, htmlToText]);

  // Handle paste - strip formatting
  const handlePaste = useCallback((e) => {
    e.preventDefault();
    const selection = window.getSelection();
    if (
      selection?.rangeCount
      && !selection.getRangeAt(0).collapsed
      && selectionIntersectsNamePill(selection.getRangeAt(0))
    ) return;

    const text = e.clipboardData.getData('text/plain');
    document.execCommand('insertText', false, text);
  }, [selectionIntersectsNamePill]);

  const handleCut = useCallback((e) => {
    const selection = window.getSelection();
    if (
      selection?.rangeCount
      && !selection.getRangeAt(0).collapsed
      && selectionIntersectsNamePill(selection.getRangeAt(0))
    ) e.preventDefault();
  }, [selectionIntersectsNamePill]);

  // Create or get drop indicator
  const getDropIndicator = useCallback(() => {
    if (!dropIndicatorRef.current) {
      const indicator = document.createElement('span');
      indicator.className = 'drop-indicator';
      indicator.innerHTML = '&nbsp;';
      dropIndicatorRef.current = indicator;
    }
    return dropIndicatorRef.current;
  }, []);

  // Remove drop indicator
  const removeDropIndicator = useCallback(() => {
    if (dropIndicatorRef.current && dropIndicatorRef.current.parentNode) {
      dropIndicatorRef.current.remove();
    }
  }, []);

  // Handle drag start
  const handleDragStart = useCallback((e) => {
    if (e.target.classList?.contains('name-pill')) {
      e.dataTransfer.setData('text/plain', '[NAME]');
      e.dataTransfer.effectAllowed = 'move';
      e.target.classList.add('dragging');
      isDraggingRef.current = true;
    }
  }, []);

  // Handle drag end
  const handleDragEnd = useCallback((e) => {
    if (e.target.classList?.contains('name-pill')) {
      e.target.classList.remove('dragging');
    }
    isDraggingRef.current = false;
    removeDropIndicator();
  }, [removeDropIndicator]);

  // Handle drag over - show live preview of drop position
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';

    if (!isDraggingRef.current) return;

    const editor = editorRef.current;
    if (!editor) return;

    // Get caret position from mouse coordinates
    const range = document.caretRangeFromPoint(e.clientX, e.clientY);
    if (!range) return;

    // Don't insert indicator inside the dragging pill or another pill
    let container = range.startContainer;
    while (container && container !== editor) {
      if (container.classList?.contains('name-pill')) return;
      container = container.parentNode;
    }

    // Remove existing indicator first
    removeDropIndicator();

    // Insert drop indicator at caret position
    const indicator = getDropIndicator();
    try {
      range.insertNode(indicator);
    } catch (err) {
      // Range might be invalid, ignore
    }
  }, [getDropIndicator, removeDropIndicator]);

  // Handle drop
  const handleDrop = useCallback((e) => {
    e.preventDefault();

    const data = e.dataTransfer.getData('text/plain');
    if (data !== '[NAME]') {
      removeDropIndicator();
      return;
    }

    // Keep the source token intact unless drag-over established a valid target.
    const dragging = editorRef.current?.querySelector('.name-pill.dragging');
    const indicator = dropIndicatorRef.current;
    if (!indicator?.parentNode || !editorRef.current?.contains(indicator)) {
      dragging?.classList.remove('dragging');
      isDraggingRef.current = false;
      removeDropIndicator();
      return;
    }

    if (dragging) dragging.remove();

    // Replace indicator with actual pill
    if (indicator.parentNode) {
      const pill = document.createElement('span');
      pill.className = 'name-pill';
      pill.contentEditable = 'false';
      pill.draggable = true;
      pill.dataset.placeholder = 'NAME';
      pill.textContent = '[NAME]';

      indicator.parentNode.replaceChild(pill, indicator);
      dropIndicatorRef.current = null;

      // Move cursor after pill
      const range = document.createRange();
      range.setStartAfter(pill);
      range.collapse(true);
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);
    }

    isDraggingRef.current = false;
    handleInput();
  }, [handleInput, removeDropIndicator]);

  // Handle drag leave
  const handleDragLeave = useCallback((e) => {
    // Only remove if leaving the editor entirely
    const rect = editorRef.current?.getBoundingClientRect();
    if (rect) {
      const { clientX, clientY } = e;
      if (
        clientX < rect.left ||
        clientX > rect.right ||
        clientY < rect.top ||
        clientY > rect.bottom
      ) {
        removeDropIndicator();
      }
    }
  }, [removeDropIndicator]);

  // Helper to find adjacent pill
  const findAdjacentPill = useCallback((node, offset, direction) => {
    if (!node) return null;

    // If we're in a text node
    if (node.nodeType === Node.TEXT_NODE) {
      if (direction === 'left' && offset === 0) {
        // Check previous sibling
        let prev = node.previousSibling;
        if (prev?.classList?.contains('name-pill')) return prev;
      } else if (direction === 'right' && offset === node.textContent.length) {
        // Check next sibling
        let next = node.nextSibling;
        if (next?.classList?.contains('name-pill')) return next;
      }
    }

    // If we're at element level
    if (node.nodeType === Node.ELEMENT_NODE) {
      const children = Array.from(node.childNodes);
      if (direction === 'left' && offset > 0) {
        const prev = children[offset - 1];
        if (prev?.classList?.contains('name-pill')) return prev;
      } else if (direction === 'right' && offset < children.length) {
        const next = children[offset];
        if (next?.classList?.contains('name-pill')) return next;
      }
    }

    return null;
  }, []);

  // Handle keydown
  const handleKeyDown = useCallback((e) => {
    onKeyDown?.(e);

    const sel = window.getSelection();
    if (!sel.rangeCount) return;

    const range = sel.getRangeAt(0);
    if (!range.collapsed) {
      const isTextReplacement = e.key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey;
      const isEditingKey = ['Backspace', 'Delete', 'Enter'].includes(e.key)
        || ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'x');
      if ((isTextReplacement || isEditingKey) && selectionIntersectsNamePill(range)) {
        e.preventDefault();
      }
      return;
    }

    const node = range.startContainer;
    const offset = range.startOffset;

    // Arrow Left - jump over pill to its left
    if (e.key === 'ArrowLeft') {
      const pill = findAdjacentPill(node, offset, 'left');
      if (pill) {
        e.preventDefault();
        const newRange = document.createRange();
        newRange.setStartBefore(pill);
        newRange.collapse(true);
        sel.removeAllRanges();
        sel.addRange(newRange);
        return;
      }
    }

    // Arrow Right - jump over pill to its right
    if (e.key === 'ArrowRight') {
      const pill = findAdjacentPill(node, offset, 'right');
      if (pill) {
        e.preventDefault();
        const newRange = document.createRange();
        newRange.setStartAfter(pill);
        newRange.collapse(true);
        sel.removeAllRanges();
        sel.addRange(newRange);
        return;
      }
    }

    // Backspace - keep protected tokens intact and move before the pill.
    if (e.key === 'Backspace') {
      const pill = findAdjacentPill(node, offset, 'left');
      if (pill) {
        e.preventDefault();
        const newRange = document.createRange();
        newRange.setStartBefore(pill);
        newRange.collapse(true);
        sel.removeAllRanges();
        sel.addRange(newRange);
        return;
      }
    }

    // Delete - keep protected tokens intact and move after the pill.
    if (e.key === 'Delete') {
      const pill = findAdjacentPill(node, offset, 'right');
      if (pill) {
        e.preventDefault();
        const newRange = document.createRange();
        newRange.setStartAfter(pill);
        newRange.collapse(true);
        sel.removeAllRanges();
        sel.addRange(newRange);
        return;
      }
    }
  }, [onKeyDown, findAdjacentPill, selectionIntersectsNamePill]);

  // Expose ref
  useEffect(() => {
    if (textareaRef && typeof textareaRef === 'function') {
      textareaRef(editorRef.current);
    } else if (textareaRef && 'current' in textareaRef) {
      textareaRef.current = editorRef.current;
    }
  }, [textareaRef]);

  return (
    <div className={`highlighted-textarea-wrapper ${className}`}>
      <div
        ref={editorRef}
        className="highlighted-textarea-editor"
        contentEditable={!disabled}
        role="textbox"
        aria-label={ariaLabel}
        aria-multiline="true"
        onInput={handleInput}
        onPaste={handlePaste}
        onCut={handleCut}
        onKeyDown={handleKeyDown}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        data-placeholder={placeholder}
        suppressContentEditableWarning
      />
    </div>
  );
}
